import { Component } from '@theme/component';

/**
 * @typedef {object} GalleryRefs
 * @property {HTMLUListElement} track
 * @property {HTMLButtonElement} toggle
 * @extends {Component<GalleryRefs>}
 */
class LpsSchoolGallery extends Component {
  #controller;
  #resizeObserver;
  #intersectionObserver;
  #motion;
  #track;
  #button;
  #copies = [];
  #frame = 0;
  #resumeTimer = 0;
  #last = 0;
  #position = 0;
  #cycle = 0;
  #holdUntil = 0;
  #visible = false;
  #hover = false;
  #focus = false;
  #paused;
  #ready = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#dispose();
    this.#setup();
  }

  disconnectedCallback() {
    this.#dispose();
    super.disconnectedCallback();
  }

  toggleScrolling() {
    if (!this.#ready) return;
    this.#paused = !this.#paused;
    this.#updateButton();
    this.#sync();
  }

  #setup() {
    if (this.#ready || !this.isConnected) return;
    const { track, toggle } = this.refs;
    if (!(track instanceof HTMLUListElement) || !(toggle instanceof HTMLButtonElement)) return;
    this.#track = track;
    this.#button = toggle;
    track.querySelectorAll(':scope > [data-lps-gallery-copy]').forEach((copy) => copy.remove());
    const items = [...track.children];
    if (!items.length) return;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused = this.#paused ?? this.#motion.matches;
    if (this.#motion.matches) this.#paused = true;
    this.#visible = false;
    this.#hover = false;
    this.#focus = track.contains(document.activeElement);
    this.#holdUntil = 0;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#copies = items.map((item) => {
      const copy = item.cloneNode(true);
      copy.setAttribute('data-lps-gallery-copy', '');
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('a').forEach((link) => { link.tabIndex = -1; });
      track.append(copy);
      return copy;
    });
    this.#ready = true;
    this.#updateButton();
    toggle.hidden = false;

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.#hover = true;
      this.#sync();
    }, { signal });
    this.addEventListener('pointerleave', () => {
      this.#hover = false;
      this.#sync();
    }, { signal });
    track.addEventListener('focusin', () => {
      this.#focus = true;
      this.#sync();
    }, { signal });
    track.addEventListener('focusout', (event) => {
      this.#focus = track.contains(event.relatedTarget);
      this.#sync();
    }, { signal });
    for (const type of ['pointerdown', 'wheel', 'keydown', 'touchstart']) {
      track.addEventListener(type, this.#hold, { passive: true, signal });
    }
    document.addEventListener('visibilitychange', this.#sync, { signal });
    this.#motion.addEventListener('change', (event) => {
      if (event.matches) this.#paused = true;
      this.#updateButton();
      this.#sync();
    }, { signal });

    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(track);
    this.#resizeObserver.observe(items[0]);
    this.#intersectionObserver = new IntersectionObserver((entries) => {
      if (!this.#ready) return;
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#sync();
    });
    this.#intersectionObserver.observe(this);
    this.#measure();
  }

  #updateButton() {
    if (!this.#button) return;
    this.#button.textContent = this.#paused ? this.dataset.resumeLabel : this.dataset.pauseLabel;
    this.#button.setAttribute('aria-pressed', String(this.#paused));
    this.dataset.scrolling = this.#paused ? 'paused' : 'playing';
  }

  #measure = () => {
    if (!this.#ready || !this.isConnected || !this.#track) return;
    const first = this.#track.firstElementChild;
    if (!first || !this.#copies[0]) return;
    this.#cycle = this.#copies[0].offsetLeft - first.offsetLeft;
    this.#sync();
  };

  #available() {
    return this.#ready && this.isConnected && this.#visible && !this.#paused &&
      !this.#hover && !this.#focus && !document.hidden && this.#cycle > 0;
  }

  #hold = () => {
    this.#holdUntil = performance.now() + 6000;
    this.#sync();
  };

  #sync = () => {
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = 0;
    if (!this.#available()) {
      this.#stop();
      return;
    }
    const remaining = this.#holdUntil - performance.now();
    if (remaining > 0) {
      this.#stop();
      this.#resumeTimer = setTimeout(this.#sync, remaining);
      return;
    }
    if (!this.#frame) {
      this.#last = 0;
      this.#position = this.#track.scrollLeft % this.#cycle;
      this.#frame = requestAnimationFrame(this.#animate);
    }
  };

  #animate = (now) => {
    this.#frame = 0;
    if (!this.#available() || now < this.#holdUntil) {
      this.#sync();
      return;
    }
    const delta = this.#last ? Math.min(now - this.#last, 50) : 0;
    this.#last = now;
    this.#position = (this.#position + delta * 0.022) % this.#cycle;
    this.#track.scrollLeft = this.#position;
    this.#frame = requestAnimationFrame(this.#animate);
  };

  #stop() {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#last = 0;
  }

  #dispose() {
    this.#ready = false;
    this.#stop();
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = 0;
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    if (this.#track && this.#cycle > 0) this.#track.scrollLeft %= this.#cycle;
    this.#copies.forEach((copy) => copy.remove());
    this.#copies = [];
    this.#cycle = 0;
    this.#visible = false;
    if (this.#button) this.#button.hidden = true;
  }
}

if (!customElements.get('lps-gallery-component')) {
  customElements.define('lps-gallery-component', LpsSchoolGallery);
}
